import { useCallback, useEffect, useState } from "react";
import { Phone, Mail, MessageCircle, Download, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const waPhone = (p) => { const d = (p || "").replace(/\D/g, ""); return d.startsWith("39") && d.length > 10 ? d : `39${d}`; };

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Nome", "Cognome", "Telefono WhatsApp", "Email", "Comune", "Consenso dal"];
  const body = rows.map((r) => [r.first_name, r.last_name, `+${waPhone(r.phone)}`, r.email, r.city, r.consent_at ? new Date(r.consent_at).toLocaleDateString("it-IT") : ""].map(esc).join(";"));
  return "\ufeff" + [head.map(esc).join(";"), ...body].join("\n");
};

function Row({ c, i, onRevoke }) {
  return (
    <div className="grid gap-3 px-5 py-4 sm:grid-cols-[1fr_auto] sm:items-center" data-testid={`marketing-row-${i}`}>
      <div>
        <p className="font-semibold text-slate-900">{c.first_name} {c.last_name}<span className="ml-2 text-xs font-normal text-slate-500">{c.city}</span></p>
        <p className="mt-0.5 flex flex-wrap gap-x-4 text-sm text-slate-500">
          <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{c.phone}</span>
          <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{c.email}</span>
        </p>
        <p className="mt-0.5 text-xs text-slate-400">Consenso dal {c.consent_at ? new Date(c.consent_at).toLocaleDateString("it-IT") : "—"} · {c.jobs} richieste</p>
      </div>
      <div className="flex gap-2">
        <a href={`https://wa.me/${waPhone(c.phone)}`} target="_blank" rel="noreferrer" data-testid={`marketing-whatsapp-${i}`}>
          <Button size="sm" className="rounded-full bg-[#25D366] hover:bg-[#1ebe5a]"><MessageCircle className="h-4 w-4" /> WhatsApp</Button>
        </a>
        <Button size="sm" variant="outline" className="rounded-full text-red-600" onClick={() => onRevoke(c)} data-testid={`marketing-revoke-${i}`}><Ban className="h-4 w-4" /> Revoca</Button>
      </div>
    </div>
  );
}

export const MarketingContacts = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/marketing-contacts").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (c) => {
    if (!window.confirm(`Registrare la revoca del consenso marketing di ${c.first_name} ${c.last_name}?`)) return;
    try {
      await api.post("/admin/marketing-contacts/revoke", { email: c.email });
      toast.success("Consenso revocato");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: `consensi-marketing-${new Date().toISOString().slice(0, 10)}.csv` });
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600" data-testid="marketing-count">{rows ? `${rows.length} clienti hanno accettato le offerte` : ""}</p>
        <Button size="sm" variant="outline" className="rounded-full" disabled={!rows?.length} onClick={exportCsv} data-testid="marketing-export-button"><Download className="h-4 w-4" /> Esporta per campagna WhatsApp</Button>
      </div>
      <div className="divide-y rounded-2xl border bg-white" data-testid="marketing-list">
        {rows === null ? <p className="p-6 text-sm text-slate-500">Caricamento…</p>
          : rows.length === 0 ? <p className="p-6 text-center text-sm text-slate-500" data-testid="marketing-empty">Nessun cliente ha ancora dato il consenso marketing.</p>
          : rows.map((c, i) => <Row key={c.email} c={c} i={i} onRevoke={revoke} />)}
      </div>
      <p className="mt-3 text-xs text-slate-500">Solo clienti che hanno spuntato il consenso facoltativo in prenotazione. Se qualcuno chiede di non ricevere più offerte (es. risponde "STOP"), usa Revoca.</p>
    </div>
  );
};
